import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/EditCustomerProfile.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import { businessAgentAPI } from "../services/businessAgentService";
import { clampAdminPhoneString, phoneErrorSuperAdmin } from "../utils/phone";

function BusinessAgentEditSection() {
  const [step, setStep] = useState(1);
  const location = useLocation();
  const agent = location.state?.agent || {};
  const navigate = useNavigate();
  const lastSelectedPhoneCountryRef = useRef(null);

  const getPhoneIso2 = (phoneCountry) => {
    if (!phoneCountry) return undefined;
    return phoneCountry.countryCode || phoneCountry.iso2 || undefined;
  };

  // State initialization with actual API data structure
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [addressLine3, setAddressLine3] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Validation states
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
  const [line1Error, setLine1Error] = useState("");
  const [cityError, setCityError] = useState("");

  // Email validation states
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [emailCheckMessage, setEmailCheckMessage] = useState("");

  // Phone validation states
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneAvailable, setPhoneAvailable] = useState(null);
  const [phoneCheckMessage, setPhoneCheckMessage] = useState("");

  // Store original values for comparison
  const [originalEmail, setOriginalEmail] = useState("");
  const [originalPhone, setOriginalPhone] = useState("");

  // Initialize form with agent data
  useEffect(() => {
    if (agent) {
      // Extract data based on your business agent API response structure
      // Assuming agent has user object with personal details
      const userData = agent.user || agent;
      
      setFirstName(userData.first_name || agent.first_name || "");
      setLastName(userData.last_name || agent.last_name || "");
      setEmail(userData.email || agent.email || "");
      setPhoneNumber(userData.phone_number || agent.phone_number || userData.mobile || "");
      
      // Address data - adjust based on your API structure
      const address = agent.address || userData.address || {};
      setAddressLine1(address.line1 || address.address_line1 || agent.address_line1 || "");
      setAddressLine2(address.line2 || address.address_line2 || agent.address_line2 || "");
      setAddressLine3(address.line3 || address.address_line3 || agent.address_line3 || "");
      setCity(address.city || agent.city || "");
      setRegion(address.region || address.state || agent.region || "");
      setCountry(address.country || agent.country || "");
      setNote(agent.status_note || agent.notes || agent.note || "");

      // Store original values
      setOriginalEmail(userData.email || agent.email || "");
      setOriginalPhone(userData.phone_number || agent.phone_number || userData.mobile || "");

      // Set status with proper formatting
      const currentStatus = agent.status || agent.current_status || 'active';
      if (currentStatus) {
        setStatus({
          value: currentStatus,
          label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1),
        });
      }
    }
  }, [agent]);

  const steps = [
    { label: "Basic details", sub: "Enter your basic details" },
    { label: "Address", sub: "Enter your address details" },
    { label: "Status", sub: "Update the status" },
  ];

  // Functions to check email/phone availability - adjust based on your API
  const checkEmailAvailability = async (email) => {
    try {
      // You might need to create a separate endpoint for business agent email check
      // For now, we'll skip this check or implement if you have the endpoint
      setEmailChecking(true);
      
      // If email unchanged, return available
      if (email === originalEmail) {
        return { available: true, message: "Email unchanged" };
      }
      
      // Simulate check - replace with actual API call if available
      // Example: await businessAgentAPI.checkEmailAvailability(email);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return { 
        available: true, 
        message: "Email available (Note: Agent emails are verified during update)" 
      };
    } catch (error) {
      console.error("Email check error:", error);
      return { available: false, message: "Error checking email" };
    } finally {
      setEmailChecking(false);
    }
  };

  const checkPhoneAvailability = async (phone) => {
    try {
      setPhoneChecking(true);
      
      if (phone === originalPhone) {
        return { available: true, message: "Phone unchanged" };
      }
      
      // Simulate check - replace with actual API call if available
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return { 
        available: true, 
        message: "Phone available (Note: Agent phones are verified during update)" 
      };
    } catch (error) {
      console.error("Phone check error:", error);
      return { available: false, message: "Error checking phone" };
    } finally {
      setPhoneChecking(false);
    }
  };

  const checkEmailAndPhoneAvailability = async () => {
    try {
      const results = await Promise.all([
        email !== originalEmail ? checkEmailAvailability(email) : Promise.resolve({ available: true, message: "" }),
        phoneNumber !== originalPhone ? checkPhoneAvailability(phoneNumber) : Promise.resolve({ available: true, message: "" })
      ]);

      if (email !== originalEmail) {
        const emailResult = results[0];
        setEmailAvailable(emailResult.available);
        setEmailCheckMessage(emailResult.message);
      }

      if (phoneNumber !== originalPhone) {
        const phoneResult = results[1];
        setPhoneAvailable(phoneResult.available);
        setPhoneCheckMessage(phoneResult.message);
      }

      return {
        emailAvailable: email !== originalEmail ? results[0].available : true,
        phoneAvailable: phoneNumber !== originalPhone ? results[1].available : true,
      };
    } catch (error) {
      console.error("Availability check error:", error);
      return {
        emailAvailable: false,
        phoneAvailable: false,
      };
    }
  };

  const nextStep = async () => {
    if (step === 1) {
      const isValid = validateBasicDetails();
      if (!isValid) return;

      const availability = await checkEmailAndPhoneAvailability();

      if (email !== originalEmail && availability.emailAvailable === false) {
        return;
      }

      if (phoneNumber !== originalPhone && availability.phoneAvailable === false) {
        return;
      }

      if (
        (email === originalEmail || availability.emailAvailable === true) &&
        (phoneNumber === originalPhone || availability.phoneAvailable === true)
      ) {
        setStep((prev) => Math.min(prev + 1, 3));
      }
    } else if (step === 2) {
      const isValid = validateAddressDetails();
      if (!isValid) return;
      setStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleStepClick = (stepNumber) => {
    if (stepNumber === step) return;
    setStep(stepNumber);
  };

  const regionOptions = {
    India: [
      "Andhra Pradesh",
      "Delhi",
      "Karnataka",
      "Maharashtra",
      "Tamil Nadu",
      "West Bengal",
    ],
    "Sri Lanka": [
      "Central Province",
      "Eastern Province",
      "Northern Province",
      "Southern Province",
      "Western Province",
      "North Central Province",
      "North Western Province",
      "Sabaragamuwa Province",
      "Uva Province",
    ],
    Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
    "United Kingdom": [
      "England - Bedfordshire",
      "England - Berkshire",
      "England - Bristol",
      "England - Buckinghamshire",
      "England - Cambridgeshire",
      "England - Cheshire",
      "England - City of London",
      "England - Cornwall",
      "England - Cumbria",
      "England - Derbyshire",
      "England - Devon",
      "England - Dorset",
      "England - Durham",
      "England - East Riding of Yorkshire",
      "England - East Sussex",
      "England - Essex",
      "England - Gloucestershire",
      "England - Greater London",
      "England - Greater Manchester",
      "England - Hampshire",
      "England - Herefordshire",
      "England - Hertfordshire",
      "England - Isle of Wight",
      "England - Kent",
      "England - Lancashire",
      "England - Leicestershire",
      "England - Lincolnshire",
      "England - Merseyside",
      "England - Norfolk",
      "England - North Yorkshire",
      "England - Northamptonshire",
      "England - Northumberland",
      "England - Nottinghamshire",
      "England - Oxfordshire",
      "England - Rutland",
      "England - Shropshire",
      "England - Somerset",
      "England - South Yorkshire",
      "England - Staffordshire",
      "England - Suffolk",
      "England - Surrey",
      "England - Sussex",
      "England - Tyne & Wear",
      "England - Warwickshire",
      "England - West Midlands",
      "England - West Sussex",
      "England - West Yorkshire",
      "England - Wiltshire",
      "England - Worcestershire",
      "Northern Ireland - Antrim",
      "Northern Ireland - Armagh",
      "Northern Ireland - Down",
      "Northern Ireland - Fermanagh",
      "Northern Ireland - Londonderry",
      "Northern Ireland - Tyrone",
      "Scotland - City of Aberdeen",
      "Scotland - Aberdeenshire",
      "Scotland - Angus",
      "Scotland - Argyll",
      "Scotland - Ayrshire",
      "Scotland - Banffshire",
      "Scotland - Berwickshire",
      "Scotland - Bute",
      "Scotland - Caithness",
      "Scotland - Clackmannanshire",
      "Scotland - Dumfriesshire",
      "Scotland - Dunbartonshire",
      "Scotland - City of Dundee",
      "Scotland - East Lothian",
      "Scotland - City of Edinburgh",
      "Scotland - Fife",
      "Scotland - City of Glasgow",
      "Scotland - Inverness-shire",
      "Scotland - Kincardineshire",
      "Scotland - Kinross-shire",
      "Scotland - Kirkcudbrightshire",
      "Scotland - Lanarkshire",
      "Scotland - Midlothian",
      "Scotland - Moray",
      "Scotland - Nairnshire",
      "Scotland - Orkney",
      "Scotland - Peeblesshire",
      "Scotland - Perthshire",
      "Scotland - Renfrewshire",
      "Scotland - Ross and Cromarty",
      "Scotland - Roxburghshire",
      "Scotland - Selkirkshire",
      "Scotland - Shetland",
      "Scotland - Stirlingshire",
      "Scotland - Sutherland",
      "Scotland - West Lothian",
      "Scotland - Wigtownshire",
      "Wales - Blaenau Gwent",
      "Wales - Bridgend",
      "Wales - Caerphilly",
      "Wales - Carmarthenshire",
      "Wales - Ceredigion",
      "Wales - Swansea",
      "Wales - Cardiff",
      "Wales - Conwy",
      "Wales - Denbighshire",
      "Wales - Flintshire",
      "Wales - Gwynedd",
      "Wales - Isle of Anglesey",
      "Wales - Merthyr Tydfil",
      "Wales - Monmouthshire",
      "Wales - Neath Port Talbot",
      "Wales - Newport",
      "Wales - Pembrokeshire",
      "Wales - Powys",
      "Wales - Rhondda Cynon Taf",
      "Wales - Torfaen",
      "Wales - Vale of Glamorgan",
      "Wales - Wrexham",
    ],
    Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
  };

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "suspended", label: "Suspended" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const validateBasicDetails = () => {
    let valid = true;
    const phoneIso2 = getPhoneIso2(lastSelectedPhoneCountryRef.current);

    if (!firstName.trim()) {
      setFirstNameError("First name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
      setFirstNameError("First name must contain only letters.");
      valid = false;
    } else if (firstName.trim().length < 3) {
      setFirstNameError("First name must be at least 3 letters long.");
      valid = false;
    } else {
      setFirstNameError("");
    }

    if (!lastName.trim()) {
      setLastNameError("Last name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
      setLastNameError("Last name must contain only letters.");
      valid = false;
    } else if (lastName.trim().length < 3) {
      setLastNameError("Last name must be at least 3 letters long.");
      valid = false;
    } else {
      setLastNameError("");
    }

    if (!email.trim()) {
      setEmailError("Email is required.");
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      if (!emailError.includes("already registered")) {
        setEmailError("");
      }
    }

    const phoneErr = phoneErrorSuperAdmin(phoneNumber, phoneIso2);
    if (phoneErr) {
      setPhoneErrorMessage(phoneErr);
      valid = false;
    } else if (!phoneErrorMessage.includes("already registered")) {
      setPhoneErrorMessage("");
    }

    return valid;
  };

  const validateAddressDetails = () => {
    let valid = true;

    if (!addressLine1.trim()) {
      setLine1Error("Address line 1 is required.");
      valid = false;
    } else {
      setLine1Error("");
    }

    if (!city.trim()) {
      setCityError("City is required.");
      valid = false;
    } else {
      setCityError("");
    }

    return valid;
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError("");

    try {
      // Get agent ID from multiple possible sources
      const agentId = agent.id || agent.agent_id || agent.user_id || agent.userId;

      if (!agentId) {
        throw new Error("Agent ID not found. Cannot update profile.");
      }

      // Prepare update data for business agent API
      const updateData = {
        first_name: firstName,
        last_name: lastName,
        email: email !== originalEmail ? email : undefined,
        phone_number: phoneNumber !== originalPhone ? phoneNumber : undefined,
        address: {
          line1: addressLine1,
          line2: addressLine2,
          line3: addressLine3,
          city: city,
          region: region,
          country: country,
        },
        notes: note || undefined,
      };

      // Remove undefined fields
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      console.log("Updating business agent with ID:", agentId, "and data:", updateData);

      // Update business agent details using the correct API
      await businessAgentAPI.updateAgentDetails(agentId, updateData);

      // If status was changed, update it separately
      const currentStatus = agent.status || agent.current_status;
      if (status && status.value !== currentStatus) {
        const isActive = status.value === 'active' || status.value === 'approved';
        await businessAgentAPI.updateAgentStatus(
          agentId, 
          isActive, 
          undefined, // commission rate if needed
          note || `Status changed to ${status.value} by admin`
        );
      }

      // Navigate back to business agent details
      navigate("/business-agent-details", {
        state: {
          agent: {
            ...agent,
            ...updateData,
            status: status?.value || currentStatus,
            message: "Business agent profile updated successfully!",
          },
        },
      });
    } catch (err) {
      console.error("Error updating business agent:", err);

      if (err.message && err.message.includes("Agent ID not found")) {
        setError("Unable to identify agent. Please return to the list and try again.");
      } else if (err.response?.data?.errors?.email) {
        setError("Email address is already taken. Please use a different email.");
      } else if (err.response?.data?.errors?.phone_number) {
        setError("Phone number is already registered. Please use a different phone number.");
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Failed to update agent profile. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original agent data
    const userData = agent.user || agent;
    const address = agent.address || userData.address || {};
    
    setFirstName(userData.first_name || agent.first_name || "");
    setLastName(userData.last_name || agent.last_name || "");
    setEmail(originalEmail);
    setPhoneNumber(originalPhone);
    setAddressLine1(address.line1 || address.address_line1 || agent.address_line1 || "");
    setAddressLine2(address.line2 || address.address_line2 || agent.address_line2 || "");
    setAddressLine3(address.line3 || address.address_line3 || agent.address_line3 || "");
    setCity(address.city || agent.city || "");
    setRegion(address.region || address.state || agent.region || "");
    setCountry(address.country || agent.country || "");
    setNote(agent.status_note || agent.notes || agent.note || "");

    const currentStatus = agent.status || agent.current_status;
    if (currentStatus) {
      setStatus({
        value: currentStatus,
        label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1),
      });
    } else {
      setStatus(null);
    }

    // Clear validation states
    setFirstNameError("");
    setLastNameError("");
    setEmailError("");
    setPhoneErrorMessage("");
    setLine1Error("");
    setCityError("");
    setError("");

    // Clear availability checks
    setEmailAvailable(null);
    setEmailCheckMessage("");
    setPhoneAvailable(null);
    setPhoneCheckMessage("");

    setStep(1);
  };

  // Get current country option for select
  const currentCountryOption = country
    ? { value: country, label: country }
    : null;
  // Get current region option for select
  const currentRegionOption = region ? { value: region, label: region } : null;

  // Keep all the JSX exactly the same as your original
  return (
    <div className="member-details-layout">
      <div className="member-details-main">
        {/* Error Message */}
        {error && (
          <div
            className="error-message"
            style={{
              margin: "1rem 0",
              padding: "0.75rem",
              background: "#f8d7da",
              color: "#721c24",
              borderRadius: "4px",
            }}
          >
            {error}
          </div>
        )}

        <div className="multi-form-container">
          <div className="progress-bar">
            {steps.map((item, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="progress-step">
                  <button
                    type="button"
                    className={`circle ${
                      isCompleted ? "completed" : isActive ? "active" : ""
                    } clickable`}
                    onClick={() => handleStepClick(stepNum)}
                    disabled={isActive}
                    title={`Go to Step ${stepNum}: ${item.label}`}
                  >
                    {isCompleted ? "✓" : stepNum}
                  </button>
                  <div className="step-text">
                    <p
                      className={`step-label ${isActive ? "active-label" : ""}`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`step-subtext ${isActive ? "active-sub" : ""}`}
                    >
                      {item.sub}
                    </p>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`line ${
                        isCompleted ? "line-filled" : "line-empty"
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Step content */}
          <div className="step-content">
            {step === 1 && (
              <div>
                <h3>Basic Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      First name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (firstNameError) setFirstNameError("");
                      }}
                      placeholder="Enter first name"
                     maxLength={255} />
                    <div className="error-container">
                      {firstNameError && (
                        <p className="error-text">{firstNameError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Last name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (lastNameError) setLastNameError("");
                      }}
                      placeholder="Enter last name"
                     maxLength={255} />
                    <div className="error-container">
                      {lastNameError && (
                        <p className="error-text">{lastNameError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEmail(value);
                        setEmailAvailable(null);
                        setEmailCheckMessage("");

                        if (
                          emailError &&
                          !emailError.includes("already registered")
                        ) {
                          setEmailError("");
                        }
                      }}
                      placeholder="Enter email"
                      className={
                        email !== originalEmail
                          ? emailAvailable === false
                            ? "email-taken"
                            : emailAvailable === true
                            ? "email-available"
                            : ""
                          : ""
                      }
                     maxLength={255} />

                    {email !== originalEmail && (
                      <>
                        {emailChecking && (
                          <div className="email-status checking">
                            Checking email availability...
                          </div>
                        )}

                        {!emailChecking && emailCheckMessage && (
                          <div
                            className={`email-status ${
                              emailAvailable === true
                                ? "available"
                                : emailAvailable === false
                                ? "taken"
                                : ""
                            }`}
                          >
                            {emailCheckMessage}
                          </div>
                        )}
                      </>
                    )}

                    <div className="error-container">
                      {emailError && <p className="error-text">{emailError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Phone Number <span className="required">*</span>
                    </label>
                    <div
                      className={`phone-input-container ${
                        phoneNumber !== originalPhone
                          ? phoneAvailable === false
                            ? "phone-taken"
                            : phoneAvailable === true
                            ? "phone-available"
                            : ""
                          : ""
                      }`}
                    >
                      <PhoneInput
                        country={"gb"}
                        enableLongNumbers
                        value={phoneNumber}
                        onChange={(value, country) => {
                          lastSelectedPhoneCountryRef.current = country || null;
                          setPhoneNumber(clampAdminPhoneString(value));

                          setPhoneAvailable(null);
                          setPhoneCheckMessage("");

                          if (
                            phoneErrorMessage &&
                            !phoneErrorMessage.includes("already registered")
                          ) {
                            setPhoneErrorMessage("");
                          }
                        }}
                        inputProps={{
                          name: "phone_number",
                          required: true,
                        }}
                        inputClass="phone-input"
                        containerClass="phone-container"
                      />
                    </div>

                    {phoneNumber !== originalPhone && (
                      <>
                        {phoneChecking && (
                          <div className="phone-status checking">
                            Checking phone availability...
                          </div>
                        )}

                        {!phoneChecking && phoneCheckMessage && (
                          <div
                            className={`phone-status ${
                              phoneAvailable === true
                                ? "available"
                                : phoneAvailable === false
                                ? "taken"
                                : ""
                            }`}
                          >
                            {phoneCheckMessage}
                          </div>
                        )}
                      </>
                    )}

                    <div className="error-container">
                      {phoneErrorMessage && <p className="error-text">{phoneErrorMessage}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3>Address</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Address line 1 <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={addressLine1}
                      onChange={(e) => {
                        setAddressLine1(e.target.value);
                        if (line1Error) setLine1Error("");
                      }}
                      placeholder="Enter address line 1"
                     maxLength={255} />
                    <div className="error-container">
                      {line1Error && <p className="error-text">{line1Error}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address line 2</label>
                    <input
                      type="text"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder="Enter address line 2"
                     maxLength={255} />
                    <div className="error-container" />
                  </div>

                  <div className="form-group full-width">
                    <label>Address line 3</label>
                    <input
                      type="text"
                      value={addressLine3}
                      onChange={(e) => setAddressLine3(e.target.value)}
                      placeholder="Enter address line 3"
                     maxLength={255} />
                    <div className="error-container" />
                  </div>

                  <div className="form-group">
                    <label>
                      Country <span className="required">*</span>
                    </label>
                    <Select
                      options={Object.keys(regionOptions).map((c) => ({
                        value: c,
                        label: c,
                      }))}
                      value={currentCountryOption}
                      onChange={(option) => {
                        setCountry(option.value);
                        setRegion("");
                        const regions = regionOptions[option.value];
                        if (regions && regions.length > 0) {
                          setRegion(regions[0]);
                        }
                      }}
                      placeholder="Select or type country"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                            ? "#e6f0ff"
                            : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container" />
                  </div>
                  <div className="form-group">
                    <label>
                      Region <span className="required">*</span>
                    </label>
                    <Select
                      options={
                        country
                          ? regionOptions[country]?.map((r) => ({
                              value: r,
                              label: r,
                            })) || []
                          : []
                      }
                      value={currentRegionOption}
                      onChange={(option) => setRegion(option.value)}
                      placeholder="Select or type region"
                      isSearchable
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                            ? "#e6f0ff"
                            : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container" />
                  </div>

                  <div className="form-group full-width">
                    <label>
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        if (cityError) setCityError("");
                      }}
                      placeholder="Enter city"
                     maxLength={255} />
                    <div className="error-container">
                      {cityError && <p className="error-text">{cityError}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3>Status</h3>
                <hr />
                <div className="form-grid center-step">
                  <div className="form-group">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      value={status}
                      onChange={setStatus}
                      placeholder="Select Status"
                      classNamePrefix="custom-select"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused
                            ? "0 0 0px rgba(21, 21, 22, 0.4)"
                            : "none",
                          "&:hover": "none",
                          fontSize: "15px",
                          color: "black",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                            ? "#e6f0ff"
                            : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      className="note-textarea"
                      rows="8"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Enter note here"
                     maxLength={65535} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="btn-row">
            {step === 3 && (
              <button
                className="btn prev"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            )}

            {step > 1 && (
              <button
                className="btn prev"
                onClick={prevStep}
                disabled={loading}
              >
                Previous
              </button>
            )}
            {step < 3 && (
              <button
                className="btn next"
                onClick={nextStep}
                disabled={loading || emailChecking || phoneChecking}
                style={{
                  opacity:
                    !loading && !emailChecking && !phoneChecking ? 1 : 0.6,
                  cursor:
                    !loading && !emailChecking && !phoneChecking
                      ? "pointer"
                      : "not-allowed",
                }}
              >
                {emailChecking || phoneChecking ? "Checking..." : "Next"}
              </button>
            )}
            {step === 3 && (
              <button
                className="btn next"
                onClick={handleUpdate}
                disabled={loading}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusinessAgentEditSection;